import { useState } from 'react'
import { useApolloClient } from '@apollo/client'
import { OneDataCollection, useDataCollectionData } from '@factorialco/f0-react/dist/experimental'
import { useAiChat } from '@factorialco/f0-react'
import { Plus } from '@factorialco/f0-react/icons/app'
import { compact } from 'lodash'
import {
  IncentivesCompensationPlanDetailDocument,
  IncentivesCompensationPlanDetailMetricsDocument,
  IncentivesCompensationPlanPeriodFragment,
  IncentivesGoalsMetricGroupFragment,
} from 'generated/resources/graphql'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useOneMutationSubscription } from 'modules/ai/lib/useOneMutationSubscription'
import { getPayoutRuleSummary } from 'modules/incentives/lib/payoutRules'
import i18n from 'modules/core/lib/i18n'
import { useEditableTable } from './useEditableTable'
import { type CompensationPlanMetric } from './types'
import { isExecutedPeriod } from 'modules/incentives/lib/period'
import { useCallback } from 'modules/core/hooks/useCallback'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useNavigate } from 'react-router-dom'

interface CompensationPlanMetricsProps {
  period: IncentivesCompensationPlanPeriodFragment
  planName: string
  periodLabel: string
  canCreateMetric: boolean
}

const isGoalsMetricGroup = (
  compensableMetric: CompensationPlanMetric['compensableMetric']
): compensableMetric is IncentivesGoalsMetricGroupFragment => {
  return compensableMetric?.__typename === 'GoalsMetricGroup'
}

export function CompensationPlanMetrics({
  period,
  planName,
  periodLabel,
  canCreateMetric,
}: CompensationPlanMetricsProps) {
  const apolloClient = useApolloClient()
  const aiChat = useAiChat()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const [refreshKey, setRefreshKey] = useState(0)
  const isExecuted = isExecutedPeriod(period)

  const openOne = (message: string) => {
    if (aiChat.enabled) {
      aiChat.setOpen(true)
      aiChat.sendMessage(message)
    }
  }

  const source = useGraphqlDataCollectionSource(
    {
      totalItemSummary: true,
      document: IncentivesCompensationPlanDetailMetricsDocument,
      perPage: 50,
      mapData: (data): CompensationPlanMetric[] => {
        const nodes = data.incentives.compensationPlanMetricsConnection.nodes ?? []
        return nodes.map((node) => {
          const { threshold, cap } = getPayoutRuleSummary(node.payoutRules)
          return { ...node, threshold: threshold ?? 0, cap }
        })
      },
      mapPagination: (data) => ({
        total: data.incentives.compensationPlanMetricsConnection.totalCount ?? 0,
      }),
      mapVariables: () => ({
        periodIds: [period.id],
      }),
      secondaryActions: {
        expanded: 1,
        actions: () =>
          compact([
            canCreateMetric && {
              label: i18n.t('incentives.detail.metrics.add_metric.label'),
              icon: Plus,
              onClick: () =>
                openOne(
                  i18n.t('incentives.detail.metrics.add_metric.one_prompt', {
                    planName,
                    periodLabel,
                  })
                ),
            },
          ]),
      },
      itemOnClick:
        ({ compensableMetric }: CompensationPlanMetric) =>
        () => {
          if (isGoalsMetricGroup(compensableMetric)) {
            navigate(navigation.goals.groups.group.fullPath({ id: compensableMetric.id }))
          }
        },
      itemActions: (item: CompensationPlanMetric) => {
        const goalName = isGoalsMetricGroup(item.compensableMetric)
          ? item.compensableMetric.title
          : ''

        return compact([
          item._policies?.delete.can && {
            label: i18n.t('incentives.detail.metrics.remove_metric.label'),
            critical: true,
            onClick: () =>
              openOne(
                i18n.t('incentives.detail.metrics.remove_metric.one_prompt', {
                  planName,
                  goalName,
                  periodLabel,
                })
              ),
          },
        ])
      },
    },
    [canCreateMetric, period.id]
  )

  const { data } = useDataCollectionData(source)

  const { editableColumns, isUpdating, onCellChange } = useEditableTable({
    period,
    metrics: data.records,
    onUpdate: useCallback(() => setRefreshKey((k) => k + 1), []),
  })

  const columns = [
    {
      id: 'name',
      label: i18n.t('incentives.detail.metrics.list.name'),
      render: (metric: CompensationPlanMetric) => {
        const compensableMetric = metric.compensableMetric
        if (isGoalsMetricGroup(compensableMetric)) {
          return {
            type: 'text' as const,
            value: compensableMetric.title,
          }
        }

        return undefined
      },
    },
    {
      id: 'progress',
      label: i18n.t('incentives.detail.metrics.list.progress'),
      render: (metric: CompensationPlanMetric) => {
        const compensableMetric = metric.compensableMetric
        if (
          isGoalsMetricGroup(compensableMetric) &&
          compensableMetric.summary?.averageProgress != null
        ) {
          return {
            type: 'progressBar' as const,
            value: {
              value: compensableMetric.summary.averageProgress,
              label: `${i18n.formatNumber(compensableMetric.summary.averageProgress)}%`,
            },
          }
        }

        return {
          type: 'text' as const,
          value: '-',
        }
      },
    },
    {
      id: 'weight',
      label: i18n.t('incentives.detail.metrics.list.weight'),
      align: 'right' as const,
      render: (metric: CompensationPlanMetric) => {
        const value = `${i18n.formatNumber(metric.weight)}%`
        if (isUpdating) return { type: 'text' as const, value }

        return {
          type: 'dotTag' as const,
          value: {
            label: value,
            color: 'grey' as const,
          },
        }
      },
      ...editableColumns.weight,
    },
    {
      id: 'threshold',
      label: i18n.t('incentives.detail.metrics.list.threshold'),
      align: 'right' as const,
      render: (metric: CompensationPlanMetric) => ({
        type: 'text' as const,
        value: metric.threshold != null ? `${i18n.formatNumber(metric.threshold)}%` : '-',
      }),
      ...editableColumns.threshold,
    },
    {
      id: 'cap',
      label: i18n.t('incentives.detail.metrics.list.cap'),
      align: 'right' as const,
      inputPlaceholder: '-',
      render: (metric: CompensationPlanMetric) => ({
        type: 'text' as const,
        value: metric.cap != null ? `${i18n.formatNumber(metric.cap)}%` : '-',
      }),
      ...editableColumns.cap,
    },
  ]

  const refetchMetrics = async () => {
    await apolloClient.refetchQueries({
      include: [
        IncentivesCompensationPlanDetailMetricsDocument,
        IncentivesCompensationPlanDetailDocument,
      ],
    })
  }

  useOneMutationSubscription('AddIncentivePlanMetrics', refetchMetrics)
  useOneMutationSubscription('RemoveIncentivePlanMetrics', refetchMetrics)
  useOneMutationSubscription('UpdateIncentivePlanMetrics', refetchMetrics)

  return (
    <OneDataCollection
      key={refreshKey} // We need this refreshKey to force the remount of data collection to remove any error state in other cells after a successful update
      fullHeight
      source={{ ...source, isLoading: isUpdating || source.isLoading }}
      visualizations={[
        isExecuted
          ? { type: 'table' as const, options: { columns } }
          : { type: 'editableTable' as const, options: { columns, onCellChange } },
      ]}
    />
  )
}
